import { Capacitor } from '@capacitor/core';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

const mutationConfig = {
  attributes: true,
  childList: true,
  subtree: true,
  attributeOldValue: true,
};

const getBottomOffset = () => {
  let offset = 0;

  const footerHight = Number(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--footer-height')
      ?.replace('px', ''),
  );

  offset = footerHight || 0;

  const saib = Number(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--safe-area-inset-bottom')
      ?.replace('px', ''),
  );

  if (saib && !Number.isNaN(saib)) {
    offset += saib;
  }

  return offset;
};

const getTopOffset = () => {
  let offset = 0;

  const safeAreaInsetTop = Number(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--safe-area-inset-top')
      ?.replace('px', ''),
  );

  if (safeAreaInsetTop && !Number.isNaN(safeAreaInsetTop)) {
    offset += safeAreaInsetTop;
  }

  return offset;
};

const getWidgetById = () =>
  document.getElementById('launcher') as HTMLIFrameElement;

const getBadgeFromWidget = (widget: HTMLIFrameElement) =>
  widget.nextSibling as HTMLIFrameElement;

const getMessageWindowByName = () =>
  document.getElementsByName(
    'Messaging window',
  ) as unknown as HTMLIFrameElement[];

/**
 * This hook aims to fix widget positioning in mobile app.
 *
 * In Zendesk console we can configure absolute positioning of the widget separately for web and mobile (mobile web and app).
 * Currently, (14.01.2025) bottom offset is set to 24px in web and 16px in mobile.
 *
 * The problem is, that in mobile app we want to move it up, so that in doesn't cover the mobile app navbar.
 * The code below does the work - it changes bottom offset for mobile app.
 */
export const useZendeskPositioningFix = () => {
  const bottomOffset = useRef(0);
  const originalWidgetBottom = useRef(0);
  const observer = useRef<MutationObserver>();

  useLayoutEffect(() => {
    bottomOffset.current = getBottomOffset();
  });

  const fixPositioning = useCallback((mutationList: MutationRecord[]) => {
    const widget = getWidgetById();
    const badge = widget && getBadgeFromWidget(widget);
    const messengerWindow = getMessageWindowByName()?.[0];

    for (const mutation of mutationList) {
      if (mutation.target === widget) {
        moveWidget(widget);
      } else if (mutation.target === badge) {
        moveBadge(badge);
      } else {
        // Unable to capture the moment when the class name of the Message window section changes
        // Therefore, capture the previous update
        // Then find and handle the Message window
        if (Capacitor.getPlatform() === 'ios') {
          moveMessageWindow(messengerWindow);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    observer.current = new MutationObserver(fixPositioning);
    observer.current.observe(document.body, mutationConfig);

    return () => {
      observer.current?.disconnect();
    };
  }, [fixPositioning]);

  const moveMessageWindow = (messageWindow: HTMLIFrameElement) => {
    if (messageWindow) {
      const topOffset = getTopOffset();

      messageWindow.style.top = `${topOffset}px`;
      messageWindow.style.height = `calc(100% - ${topOffset}px)`;
    }
  };

  const moveWidget = (widget: HTMLIFrameElement) => {
    if (widget) {
      const suggestionDiv = widget.previousSibling
        ?.previousSibling as HTMLIFrameElement;

      if (originalWidgetBottom.current === 0) {
        originalWidgetBottom.current = Number(
          widget.style.bottom?.replace('px', ''),
        );
        bottomOffset.current += originalWidgetBottom.current;
      }

      widget.style.bottom = `${bottomOffset.current}px`;

      if (suggestionDiv) {
        suggestionDiv.style.bottom = `${bottomOffset.current + 72}px`;
      }
    }
  };

  const moveBadge = (badge: HTMLIFrameElement) => {
    if (badge) {
      badge.style.bottom = `${bottomOffset.current + 48}px`;
    }
  };
};
